import { Routes, Route, Navigate } from 'react-router-dom';
import HomePage from '@/modules/Home/pages/Home';
import ElementDetectionPage from '@/modules/IA/pages/ElementDetection';
import DamageDetectionPage from '@/modules/IA/pages/DamageDetection';
import OrientationDetectionPage from '@/modules/IA/pages/OrientationDetection';
import RemoveBackgroundPage from '@/modules/IA/pages/RemoveBackground';
import RegistrationCarReadingPage from '@/modules/IA/pages/RegistrationCarReading';
import HistoryPage from '@/modules/IA/pages/History';
import LegalMentionPage from '@/modules/LegalInfos/pages/LegalMention';
import PrivacyPolicyPage from '@/modules/LegalInfos/pages/PrivacyPolicy';
import CguPage from '@/modules/LegalInfos/pages/Cgu';
import { EngineCorrectionPage } from '@/modules/IA/pages/EngineCorrection';
import { BackOfficeLayout } from '@/layouts/BackOfficeLayout';
import ProtectedRoute from './ProtectedRoute';
import { Register } from '@/modules/Auth/Register/pages/Register';
import { Login } from '@/modules/Auth/Login/pages/Login';
import { ForgotPassword } from '@/modules/Auth/ForgotPassword/pages/ForgotPassword';
import { ResetPassword } from '@/modules/Auth/ResetPassword/pages/ResetPassword';
import ReadCarteGrisePage from '@/modules/IA/pages/ReadCarteGrise';
import { EngineCorrectionListPage } from '@/modules/IA/pages/EngineCorrectionList';
import EngineCorrectionProvider from '@/providers/EngineCorrectionProvider';
import KPIMoteur from '@/modules/Administration/pages/KpiMoteur';
import EvolutionKPIMoteur from '@/modules/Administration/pages/EvolutionKPIMoteur';
import { GroupListPage } from '@/modules/Structure/Group/pages/List';
import { CreateUserPage } from '@/modules/User/pages/Create';
import { UserListPage } from '@/modules/User/pages/List';
import { EditUserPage } from '@/modules/User/pages/Edit';
import { CreateBranchPage } from '@/modules/Structure/Branch/pages/Create';
import { BranchListPage } from '@/modules/Structure/Branch/pages/List';
import { EditBranchPage } from '@/modules/Structure/Branch/pages/Edit';
import { CreateGroupPage } from '@/modules/Structure/Group/pages/Create';
import { EditGroupPage } from '@/modules/Structure/Group/pages/Edit';
import { CreateCompanyPage } from '@/modules/Structure/Company/pages/Create';
import { CompanyListPage } from '@/modules/Structure/Company/pages/List';
import { EditCompanyPage } from '@/modules/Structure/Company/pages/Edit';
import { EditVehiculeCheckPage } from '@/modules/VehiculeCheck/pages/Edit';
import { VehiculeCheckListPage } from '@/modules/VehiculeCheck/pages/List';
import { CreateVehiculePage } from '@/modules/Vehicule/pages/Create';
import { VehiculeListPage } from '@/modules/Vehicule/pages/List';
import { EditVehiculePage } from '@/modules/Vehicule/pages/Edit';
import { ViewVehiculeCheckPage } from '@/modules/VehiculeCheck/pages/ViewDetails';
import { CreateVehiculeCheckRequestPage } from '@/modules/VehiculeCheck/pages/CreateRequest';
import { CreateEquipmentPage } from '@/modules/Equipment/pages/Create';
import { EquipmentListPage } from '@/modules/Equipment/pages/List';
import { EditEquipmentPage } from '@/modules/Equipment/pages/Edit';
import { CreateDamagePage } from '@/modules/Damage/pages/Create';
import { DamageListPage } from '@/modules/Damage/pages/List';
import { EditDamagePage } from '@/modules/Damage/pages/Edit';
import { EditVehiculeCheckRequestPage } from '@/modules/VehiculeCheck/pages/EditRequest';
import { ViewVehiculeCheckEmailPage } from '@/modules/VehiculeCheckEmail/pages/ViewDetails';
import { CreateCheckPointPage } from '@/modules/CheckPoint/pages/Create';
import { CheckPointListPage } from '@/modules/CheckPoint/pages/List';
import { EditCheckPointPage } from '@/modules/CheckPoint/pages/Edit';
import { CreateVehiculeCheckType } from '@/modules/VehiculeCheckType/pages/Create';
import { VehiculeCheckTypeListPage } from '@/modules/VehiculeCheckType/pages/List';
import { EditVehiculeCheckTypePage } from '@/modules/VehiculeCheckType/pages/Edit';
import { CreateEmailTemplatePage } from '@/modules/TemplateEmail/pages/Create';
import { TemplateEmailListPage } from '@/modules/TemplateEmail/pages/List';
import { EditEmailTemplatePage } from '@/modules/TemplateEmail/pages/Edit';
import { GlobalEmailSettingPage } from '@/modules/GlobalEmailSetting/pages/Setting';
import { RoleName } from '@/api/entities/user-role-type';
import { RoleProtectedRoute } from './RoleProtectedRoute';
import { AllSelectedTableItemsProvider } from '@/providers/AllSelectedTableItemsProvider';
import { useAuth, useHideThemeOnInit } from '@/hooks/auth.hooks';
import { getAccessToken } from '@/utils/token.utils';
import { useScrollToTopOnUrlChange } from '@/hooks/scroll-to-top.hooks';
import { twMerge } from 'tailwind-merge';
import { ResultsPreviewPdfPage } from '@/modules/VehiculeCheck/pages/PreviewPdf';
import { InspectionUsageListPage } from '@/modules/VehiculeCheck/pages/ListUsage';
// import { ViewVehiculeCheckTab } from '@/type/ui/tab/vehiculeCheckTab';
import { EditGroupTab } from '@/type/ui/tab/groupTab';
import { RequestAppUpdate } from '@/modules/RequestedAppUpdate/pages/Request';
import { EndpointHistoryListPage } from '@/modules/EndpointHistory/pages/List';
import { CreateRepairServicePage } from '@/modules/RepairService/pages/Create';
import { EditRepairServicePage } from '@/modules/RepairService/pages/Edit';
import { RepairServiceListPage } from '@/modules/RepairService/pages/List';
import { RepairCostingListPage } from '@/modules/RepairCosting/pages/List';
import { CreateRepairCostingPage } from '@/modules/RepairCosting/pages/Create';
import { EditRepairCostingPage } from '@/modules/RepairCosting/pages/Edit';

const roleSessionStorage = sessionStorage.getItem('role');

const protectedRoutes = [
  {
    path: '/dashboard',
    name: 'Home',
    element: <HomePage />,
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/detect-car-element',
    name: 'ElementDetection',
    element: <ElementDetectionPage />,
  },
  {
    path: '/read-carte-grise',
    name: 'ReadCarteGrise',
    element: <ReadCarteGrisePage />,
  },
  {
    path: '/detect-car-damage',
    name: 'DamageDetection',
    element: <DamageDetectionPage />,
  },
  {
    path: '/detect-car-orientation',
    name: 'OrientationDetection',
    element: <OrientationDetectionPage />,
  },
  {
    path: '/remove-car-background',
    name: 'RemoveBackground',
    element: <RemoveBackgroundPage />,
  },
  {
    path: '/reading-car-registration-id',
    name: 'RegistrationCarReading',
    element: <RegistrationCarReadingPage />,
  },
  { path: '/history', name: 'History', element: <HistoryPage /> },
  {
    path: '/moteur-de-correction-liste',
    name: 'EngineCorrectionList',
    element: (
      <EngineCorrectionProvider>
        <EngineCorrectionListPage />
      </EngineCorrectionProvider>
    ),
    allowedRoles: [RoleName.ADMIN, RoleName.MODERATOR],
  },
  {
    path: '/engine-correction/:id',
    name: 'EngineCorrection',
    element: (
      <EngineCorrectionProvider>
        <EngineCorrectionPage />
      </EngineCorrectionProvider>
    ),
    allowedRoles: [RoleName.ADMIN, RoleName.MODERATOR],
  },
  {
    path: '/kpi-moteur',
    name: 'EngineKPI',
    element: <KPIMoteur />,
  },
  {
    path: '/evolution-kpi-moteur',
    name: 'EvolutionKPIMoteur',
    element: <EvolutionKPIMoteur />,
  },
  {
    path: '/users',
    name: 'UserList',
    element: (
      <AllSelectedTableItemsProvider>
        <UserListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [
      RoleName.MODERATOR,
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/user/create',
    name: 'CreateUser',
    element: <CreateUserPage />,
    allowedRoles: [RoleName.GROUP_MANAGER],
  },
  {
    path: '/user/edit/:id',
    name: 'EditUser',
    element: <EditUserPage />,
    allowedRoles: [
      RoleName.MODERATOR,
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/branches',
    name: 'BranchList',
    element: (
      <AllSelectedTableItemsProvider>
        <BranchListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/branches/group/:groupId',
    name: 'BranchListByGroupId',
    element: (
      <AllSelectedTableItemsProvider>
        <BranchListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
    ],
  },
  {
    path: '/branches/company/:companyId',
    name: 'BranchListByCompanyId',
    element: (
      <AllSelectedTableItemsProvider>
        <BranchListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
    ],
  },
  {
    path: '/branch/create',
    name: 'CreateBranch page',
    element: <CreateBranchPage />,
    allowedRoles: [RoleName.GROUP_MANAGER, RoleName.COMPANY_MANAGER],
  },
  {
    path: '/branch/edit/:id',
    name: 'EditBranch',
    element: <EditBranchPage />,
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
    ],
  },
  {
    path: '/groups',
    name: 'GroupList',
    element: (
      <AllSelectedTableItemsProvider>
        <GroupListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [RoleName.MODERATOR, RoleName.GROUP_MANAGER],
  },
  {
    path: '/group/create',
    name: 'CreateGroup',
    element: <CreateGroupPage />,
  },
  roleSessionStorage === RoleName.MODERATOR && {
    path: '/group/edit/:id',
    name: 'EditGroupReport',
    element: <EditGroupPage />,
    allowedQueries: [
      {
        tab: EditGroupTab.REPORT,
      },
    ],
    allowedRoles: [RoleName.MODERATOR],
  },
  {
    path: '/group/edit/:id',
    name: 'EditGroup',
    element: <EditGroupPage />,
    allowedRoles: [RoleName.GROUP_MANAGER],
  },
  {
    path: '/companies',
    name: 'CompanyList',
    element: (
      <AllSelectedTableItemsProvider>
        <CompanyListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [RoleName.GROUP_MANAGER, RoleName.COMPANY_MANAGER],
  },
  {
    path: '/companies/group/:groupId',
    name: 'CompanyListByGroupId',
    element: (
      <AllSelectedTableItemsProvider>
        <CompanyListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [RoleName.GROUP_MANAGER, RoleName.COMPANY_MANAGER],
  },
  {
    path: '/company/create',
    name: 'CreateCompany',
    element: <CreateCompanyPage />,
    allowedRoles: [RoleName.GROUP_MANAGER],
  },
  {
    path: '/company/edit/:id',
    name: 'EditCompany',
    element: <EditCompanyPage />,
    allowedRoles: [RoleName.GROUP_MANAGER, RoleName.COMPANY_MANAGER],
  },
  {
    path: '/vehicule-check-request/create/vehicule/:vehiculeId',
    name: 'CreateVehiculeCheck',
    element: <CreateVehiculeCheckRequestPage />,
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/vehicule-check-request/create',
    name: 'CreateVehiculeCheck',
    element: <CreateVehiculeCheckRequestPage />,
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/vehicule-check-request/:id',
    name: 'EditVehiculeCheckRequest',
    element: <EditVehiculeCheckRequestPage />,
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/vehicule-checks',
    name: 'VehiculeCheckList',
    element: (
      <AllSelectedTableItemsProvider>
        <VehiculeCheckListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [
      RoleName.MODERATOR,
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/vehicule-checks/vehicule/:vehiculeId',
    name: 'VehiculeCheckListByVehiculeId',
    element: (
      <AllSelectedTableItemsProvider>
        <VehiculeCheckListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [RoleName.GROUP_MANAGER, RoleName.COMPANY_MANAGER],
  },
  {
    path: '/vehicule-check-requests',
    name: 'VehiculeCheckRequestList',
    element: (
      <AllSelectedTableItemsProvider>
        <VehiculeCheckListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [
      RoleName.MODERATOR,
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/vehicule-check/edit/:id',
    name: 'EditVehiculeCheck',
    element: <EditVehiculeCheckPage />,
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/vehicule-check-request/edit/:id',
    name: 'EditVehiculeCheckRequest',
    element: <EditVehiculeCheckPage />,
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  // roleSessionStorage === RoleName.MODERATOR && {
  //   path: '/vehicule-check/view/:id',
  //   name: 'ViewVehiculeCheckDetailedResults',
  //   element: <ViewVehiculeCheckPage />,
  //   allowedQueries: [
  //     {
  //       tab: ViewVehiculeCheckTab.DETAILED_RESULTS,
  //     },
  //   ],
  //   allowedRoles: [RoleName.MODERATOR],
  // },
  // {
  //   path: '/vehicule-check/view/:id',
  //   name: 'ViewVehiculeCheck',
  //   element: <ViewVehiculeCheckPage />,
  //   allowedRoles: [RoleName.GROUP_MANAGER, RoleName.COMPANY_MANAGER],
  // },
  {
    path: '/vehicule-check/preview/results/pdf/:id',
    name: 'ResultsPreviewPdf',
    element: <ResultsPreviewPdfPage />,
    allowedRoles: [RoleName.GROUP_MANAGER, RoleName.COMPANY_MANAGER],
  },
  {
    path: '/vehicule-check/usage',
    name: 'InspectionUsageList',
    element: (
      <AllSelectedTableItemsProvider>
        <InspectionUsageListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [
      RoleName.MODERATOR,
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/vehicule/create',
    name: 'CreateVehicule',
    element: <CreateVehiculePage />,
    allowedRoles: [RoleName.GROUP_MANAGER, RoleName.COMPANY_MANAGER],
  },
  {
    path: '/vehicules',
    name: 'VehiculeList',
    element: (
      <AllSelectedTableItemsProvider>
        <VehiculeListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/vehicule/edit/:id',
    name: 'EditVehicule',
    element: <EditVehiculePage />,
    allowedRoles: [RoleName.GROUP_MANAGER, RoleName.COMPANY_MANAGER],
  },
  {
    path: '/equipment/create',
    name: 'CreateEquipment',
    element: <CreateEquipmentPage />,
  },
  {
    path: '/equipments',
    name: 'EquipmentList',
    element: (
      <AllSelectedTableItemsProvider>
        <EquipmentListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [RoleName.MODERATOR],
  },
  {
    path: '/equipment/edit/:id/processing/:processingId',
    name: 'EditEquipmentWithProcessing',
    element: <EditEquipmentPage />,
    allowedRoles: [RoleName.MODERATOR],
  },
  {
    path: '/equipment/edit/:id',
    name: 'EditEquipment',
    element: <EditEquipmentPage />,
    allowedRoles: [RoleName.MODERATOR],
  },
  {
    path: '/damage/create',
    name: 'EditDamage',
    element: <CreateDamagePage />,
  },
  {
    path: '/damages',
    name: 'DamageList',
    element: (
      <AllSelectedTableItemsProvider>
        <DamageListPage />
      </AllSelectedTableItemsProvider>
    ),
  },
  {
    path: '/damage/edit/:id',
    name: 'EditDamage',
    element: <EditDamagePage />,
  },
  {
    path: '/vehicule-check-email/view/:id',
    name: 'ViewEmail',
    element: <ViewVehiculeCheckEmailPage />,
    allowedRoles: [
      RoleName.GROUP_MANAGER,
      RoleName.COMPANY_MANAGER,
      RoleName.BRANCH_MANAGER,
      RoleName.BRANCH_AGENT,
    ],
  },
  {
    path: '/check-point/create',
    name: 'CreateCheckPoint',
    element: <CreateCheckPointPage />,
  },
  {
    path: '/check-points',
    name: 'CheckPointList',
    element: (
      <AllSelectedTableItemsProvider>
        <CheckPointListPage />
      </AllSelectedTableItemsProvider>
    ),
  },
  {
    path: '/check-point/edit/:id',
    name: 'EditCheckPoint',
    element: <EditCheckPointPage />,
  },
  {
    path: '/vehicule-check-type/create',
    name: 'CreateVehiculeCheckType',
    element: <CreateVehiculeCheckType />,
  },
  {
    path: '/vehicule-check-types',
    name: 'VehiculeCheckTypeList',
    element: (
      <AllSelectedTableItemsProvider>
        <VehiculeCheckTypeListPage />
      </AllSelectedTableItemsProvider>
    ),
  },
  {
    path: '/vehicule-check-type/edit/:id',
    name: 'EditVehiculeCheckType',
    element: <EditVehiculeCheckTypePage />,
  },
  {
    path: '/email-template/create',
    name: 'CreateEmailTemplate',
    element: <CreateEmailTemplatePage />,
  },
  {
    path: '/email-templates',
    name: 'EmailTemplateList',
    element: (
      <AllSelectedTableItemsProvider>
        <TemplateEmailListPage />
      </AllSelectedTableItemsProvider>
    ),
  },
  {
    path: '/email-template/edit/:id',
    name: 'EditEmailTemplate',
    element: <EditEmailTemplatePage />,
  },
  {
    path: '/email-settings',
    name: 'GlobalEmailSetting',
    element: <GlobalEmailSettingPage />,
    allowedRoles: [RoleName.ADMIN],
  },
  {
    path: '/requested-app-update',
    name: 'EditRequestedAppUpdate',
    element: <RequestAppUpdate />,
    allowedRoles: [RoleName.ADMIN],
  },
  {
    path: '/repair-services',
    name: 'RepairServiceList',
    element: (
      <AllSelectedTableItemsProvider>
        <RepairServiceListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [RoleName.ADMIN],
  },
  {
    path: '/repair-service/create',
    name: 'CreateRepairService',
    element: <CreateRepairServicePage />,
    allowedRoles: [RoleName.ADMIN],
  },
  {
    path: '/repair-service/edit/:id',
    name: 'EditRepairService',
    element: <EditRepairServicePage />,
    allowedRoles: [RoleName.ADMIN],
  },
  {
    path: '/repair-costings',
    name: 'RepairCostingList',
    element: (
      <AllSelectedTableItemsProvider>
        <RepairCostingListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [RoleName.ADMIN],
  },
  {
    path: '/repair-costing/create',
    name: 'CreateRepairCosting',
    element: <CreateRepairCostingPage />,
    allowedRoles: [RoleName.ADMIN],
  },
  {
    path: '/repair-costing/edit/:id',
    name: 'EditRepairCosting',
    element: <EditRepairCostingPage />,
    allowedRoles: [RoleName.ADMIN],
  },
  {
    path: '/cgu',
    name: 'Cgu',
    element: <CguPage />,
    allowedRoles: [RoleName.GROUP_MANAGER],
  },
  {
    path: '/legal-mention',
    name: 'LegalMention',
    element: <LegalMentionPage />,
    allowedRoles: [RoleName.GROUP_MANAGER],
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    element: <PrivacyPolicyPage />,
    allowedRoles: [RoleName.GROUP_MANAGER],
  },
  {
    path: '/endpoints',
    name: 'EndpointHistoryList',
    element: (
      <AllSelectedTableItemsProvider>
        <EndpointHistoryListPage />
      </AllSelectedTableItemsProvider>
    ),
    allowedRoles: [RoleName.ADMIN],
  },
];

const AppRoutes = () => {
  const { loggedInUser } = useHideThemeOnInit();
  const { isRestoring } = useAuth();
  useScrollToTopOnUrlChange();

  return (
    <Routes>
      <Route element={<ProtectedRoute />}>
        {protectedRoutes.map(
          ({ path, element, allowedQueries, allowedRoles }, key) => {
            return (
              <Route
                path={path}
                key={key}
                element={
                  <div className={twMerge('hidden', loggedInUser && 'block')}>
                    <BackOfficeLayout hasCustomHeader={path === '/dashboard'}>
                      <RoleProtectedRoute
                        allowedRoles={allowedRoles}
                        element={element}
                        allowedQueries={allowedQueries}
                      />
                    </BackOfficeLayout>
                  </div>
                }
              />
            );
          },
        )}
      </Route>
      <Route
        path="/"
        element={
          isRestoring ? null : !loggedInUser && !getAccessToken() ? (
            <Navigate to="/login" replace />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        }
      />
      <Route
        path="*"
        element={isRestoring ? null : <Navigate to="/" replace />}
      />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route
        path="/vehicule-check/view/:id"
        element={
          loggedInUser ? (
            <BackOfficeLayout>
              <ViewVehiculeCheckPage />
            </BackOfficeLayout>
          ) : (
            <div className="container-fixed py-10">
              <ViewVehiculeCheckPage />
            </div>
          )
        }
      />
      <Route
        path="/vehicule-check-request/view/:id"
        element={
          loggedInUser ? (
            <BackOfficeLayout>
              <ViewVehiculeCheckPage />
            </BackOfficeLayout>
          ) : (
            <div className="container-fixed py-10">
              <ViewVehiculeCheckPage />
            </div>
          )
        }
      />
    </Routes>
  );
};

export default AppRoutes;
